import axios from 'axios';

// Get API URL from environment or derive from current browser host
const API_URL = process.env.REACT_APP_API_URL || ``;
console.log(`API_URL set to: ${API_URL}`);

// Create a separate instance for admin API calls
const adminAxios = axios.create({
  baseURL: API_URL
});

// Add request interceptor to include auth token for admin API calls
adminAxios.interceptors.request.use(config => {
  const token = localStorage.getItem('adminToken');
  
  if (token) {
    // Verify token before using it
    if (verifyToken(token)) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Token is invalid or expired, clear it
      console.warn('Admin token expired or invalid, redirecting to login');
      localStorage.removeItem('adminToken');
      
      // Force redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/admin/login';
      }
    }
  }
  return config;
}, error => {
  return Promise.reject(error);
});

// Add token verification function
const verifyToken = (token) => {
  if (!token) return false;
  
  try {
    // Basic validation - check if token is in expected format
    // This doesn't validate the signature, just checks for JWT structure
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Check if token has expired
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    
    return Date.now() < exp;
  } catch (error) {
    console.error('Token verification error:', error);
    return false;
  }
};

const apiService = {
  /**
   * Send a question to the backend and get an answer
   * @param {string} question - The question to ask
   * @param {string} sessionId - Session ID for tracking conversation
   * @param {Array} selectedSources - Optional list of sources to filter query by
   * @param {Array} conversationHistory - Optional conversation history for context
   * @returns {Promise} - Promise with the answer data including contributing chunks
   */  askQuestion: async (question, sessionId, selectedSources = null, conversationHistory = null) => {
    try {
      let enhancedQuestion = question;
      
      // Add conversation context directly to the question if conversation history exists
      if (conversationHistory && conversationHistory.length > 0) {
        // Get the last 4 messages (2 recent exchanges) for context
        const recentMessages = conversationHistory.slice(-4);
        
        if (recentMessages.length > 0) {
          const contextPart = recentMessages.map(msg => 
            `${msg.sender === 'user' ? 'User' : 'Assistant'}: ${msg.text}`
          ).join('\n');
          
          enhancedQuestion = `Previous conversation context:\n${contextPart}\n\nCurrent question: ${question}`;
        }
      }
      
      const requestData = {
        question: enhancedQuestion,
        id: sessionId
      };
      
      // Add selected sources to the request if provided
      if (selectedSources && selectedSources.length > 0) {
        // Handle multiple sources properly
        if (selectedSources.length === 1) {
          // For a single source, use direct filtering
          requestData.filter = {
            source: selectedSources[0]
          };
        } else {
          // For multiple sources, send a special filter format that the backend understands
          requestData.filter = {
            source_multi: selectedSources
          };
        }
      }
      
      const response = await axios.post(`${API_URL}/ask-agent`, requestData);
      
      // Return the full response data, including any contributing chunks
      return response.data;
    } catch (error) {
      console.error('Error asking question:', error);
      throw error;
    }
  },

  /**
   * Upload documents to be ingested by the system
   * @param {FormData} formData - FormData containing the files
   * @returns {Promise} - Promise with the upload response
   */
  uploadDocuments: async (formData) => {
    try {
      // First try the /documents/upload endpoint, as it's recommended in the backend
      try {
        const response = await axios.post(`${API_URL}/documents/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        return response.data;
      } catch (error) {
        // If the /documents/upload endpoint fails, try the legacy /documents endpoint
        console.log("Falling back to legacy /documents endpoint");
        const response = await axios.post(`${API_URL}/documents`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  },
  /**
 * Send feedback about an answer
 * @param {string} user - User ID or username
 * @param {string} question - The question asked
 * @param {string} answer - The answer received
 * @param {string} comments - User comments about the answer
 * @param {string} feedtype - Feedback type (Like, Dislike)
 * @param {string} feedbackCategory - Category of feedback (improvement, question, bug)
 * @param {string} session_user - Session user for negative feedback
 * @returns {Promise} - Promise with the feedback response
 */
sendFeedback: async (user, question, answer, comments, feedtype, feedbackCategory = "", session_user = "") => {
  try {
    const requestBody = {
      user,
      question,
      answer,
      comments,
      feedtype,
      feedbackCategory
    };

    // Only add session_user if it's provided and feedtype is Dislike
    if (feedtype === "Dislike" && session_user) {
      requestBody.session_user = session_user;
    }

    const response = await axios.post(`${API_URL}/search/feedback`, requestBody);
    return response.data;
  } catch (error) {
    console.error('Error sending feedback:', error);
    throw error;
  }
},

  /**
   * Get list of ingested files
   * @returns {Promise} - Promise with the list of files
   */
  getFilesList: async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/list`);
      return response.data;
    } catch (error) {
      console.error('Error getting files list:', error);
      throw error;
    }
  },

  /**
   * Get list of available LLM models
   * @returns {Promise} - Promise with the list of available models and current model
   */
  getAvailableModels: async () => {
    try {
      const response = await axios.get(`${API_URL}/models`);
      return response.data;
    } catch (error) {
      console.error('Error getting available models:', error);
      throw error;
    }
  },

  /**
   * Switch to a different LLM model
   * @param {string} modelId - ID of the model to switch to
   * @returns {Promise} - Promise with the switch response
   */
  switchModel: async (modelId) => {
    try {
      const formData = new FormData();
      formData.append('model_id', modelId);
      const response = await axios.post(`${API_URL}/models/active`, formData);
      return response.data;
    } catch (error) {
      console.error('Error switching model:', error);
      throw error;
    }
  },

  /**
   * Delete a document by filename
   * @param {string} filename - Name of the file to delete
   * @returns {Promise} - Promise with the deletion response
   */
  deleteDocument: async (filename) => {
    try {
      const response = await axios.delete(`${API_URL}/documents/${encodeURIComponent(filename)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  /**
   * Evaluate all available models using provided test questions and use cases
   * @param {Array<string>} testQuestions - List of test questions to evaluate models with
   * @param {Array<string>} useCases - Optional list of use cases to evaluate models for
   * @returns {Promise} - Promise with evaluation results
   */
  evaluateModels: async (testQuestions, useCases = null) => {
    try {
      const response = await axios.post(`${API_URL}/models/evaluate`, {
        test_questions: testQuestions,
        use_cases: useCases
      });
      return response.data;
    } catch (error) {
      console.error('Error evaluating models:', error);
      throw error;
    }
  },

  /**
   * Get all previously saved model evaluations
   * @returns {Promise} - Promise with model evaluation results
   */
  getModelEvaluations: async () => {
    try {
      const response = await axios.get(`${API_URL}/models/evaluations`);
      return response.data;
    } catch (error) {
      console.error('Error getting model evaluations:', error);
      throw error;
    }
  },

  /**
   * Get a recommended model for a specific use case and priority
   * @param {string} useCase - The use case to get a recommendation for
   * @param {string} priority - Priority factor ('performance', 'cost', or 'balanced')
   * @returns {Promise} - Promise with the recommended model
   */
  getModelRecommendation: async (useCase, priority = 'balanced') => {
    try {
      const formData = new FormData();
      formData.append('use_case', useCase);
      formData.append('priority', priority);
      const response = await axios.post(`${API_URL}/models/recommend`, formData);
      return response.data;
    } catch (error) {
      console.error('Error getting model recommendation:', error);
      throw error;
    }
  },

  /**
   * Get list of embeddings with pagination and filtering
   * @param {string} filename - Optional filename filter
   * @param {number} limit - Maximum number of embeddings to return
   * @param {number} skip - Number of embeddings to skip for pagination
   * @param {string} fileType - Optional file type filter
   * @returns {Promise} - Promise with the embeddings data
   */
  getEmbeddings: async (filename = null, limit = 100, skip = 0, fileType = null) => {
    try {
      const params = { limit, skip };
      if (filename) params.filename = filename;
      if (fileType) params.file_type = fileType;
      
      const response = await axios.get(`${API_URL}/embeddings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting embeddings:', error);
      throw error;
    }
  },

  /**
   * Get detailed information about a specific embedding
   * @param {string} embeddingId - ID of the embedding to retrieve
   * @returns {Promise} - Promise with the embedding details
   */
  getEmbeddingDetail: async (embeddingId) => {
    try {
      const response = await axios.get(`${API_URL}/embeddings/${embeddingId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting embedding detail:', error);
      throw error;
    }
  },

  /**
   * Get statistics about the embeddings database
   * @returns {Promise} - Promise with embeddings statistics
   */
  getEmbeddingsStats: async () => {
    try {
      const response = await axios.get(`${API_URL}/embeddings/stats`);
      return response.data;
    } catch (error) {
      console.error('Error getting embeddings stats:', error);
      throw error;
    }
  },

  /**
   * Get all model configurations
   * @returns {Promise} - Promise with all model configurations
   */
  getModelConfigurations: async () => {
    try {
      const response = await axios.get(`${API_URL}/configurations`);
      return response.data;
    } catch (error) {
      console.error('Error getting model configurations:', error);
      throw error;
    }
  },

  /**
   * Get configuration for a specific model platform
   * @param {string} platform - The platform to get configuration for (openai, azure_openai, gemini)
   * @returns {Promise} - Promise with the platform configuration
   */
  getModelConfiguration: async (platform) => {
    try {
      const response = await axios.get(`${API_URL}/configurations/${platform}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${platform} configuration:`, error);
      throw error;
    }
  },

  /**
   * Save a new model configuration
   * @param {Object} configData - Configuration data including platform and platform-specific settings
   * @returns {Promise} - Promise with the save response
   */
  saveModelConfiguration: async (configData) => {
    try {
      const response = await axios.post(`${API_URL}/configurations`, configData);
      return response.data;
    } catch (error) {
      console.error('Error saving model configuration:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing model configuration
   * @param {Object} configData - Configuration data including id, platform and platform-specific settings
   * @returns {Promise} - Promise with the update response
   */
  updateModelConfiguration: async (configData) => {
    try {
      const response = await axios.put(`${API_URL}/configurations/${configData.id}`, configData);
      return response.data;
    } catch (error) {
      console.error('Error updating model configuration:', error);
      throw error;
    }
  },
  
  /**
   * Delete a model configuration
   * @param {string} configId - ID of the configuration to delete
   * @returns {Promise} - Promise with the delete response
   */
  deleteModelConfiguration: async (configId) => {
    try {
      const response = await axios.delete(`${API_URL}/configurations/${configId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting model configuration:', error);
      throw error;
    }
  },

  /**
   * Apply a saved model configuration
   * @param {string} configId - ID of the configuration to apply
   * @returns {Promise} - Promise with the apply response
   */
  applyModelConfiguration: async (configId) => {
    try {
      const response = await axios.post(`${API_URL}/configurations/${configId}/apply`);
      return response.data;
    } catch (error) {
      console.error('Error applying model configuration:', error);
      throw error;
    }
  },

  /**
   * Create a keyword index for the vector store
   * @returns {Promise} - Promise with the indexing response
   */
  createKeywordIndex: async () => {
    try {
      const response = await axios.post(`${API_URL}/documents/index`);
      return response.data;
    } catch (error) {
      console.error('Error creating keyword index:', error);
      throw error;
    }
  },

  /**
   * Perform a hybrid search
   * @param {string} query - The search query
   * @returns {Promise} - Promise with the search results
   */
  hybridSearch: async (query) => {
    try {
      const formData = new FormData();
      formData.append('query', query);
      const response = await axios.post(`${API_URL}/search/hybrid`, formData);
      return response.data;
    } catch (error) {
      console.error('Error performing hybrid search:', error);
      throw error;
    }
  },

  /**
   * Perform a similarity search
   * @param {Object} searchParams - The search parameters
   * @returns {Promise} - Promise with the search results
   */
  similaritySearch: async (searchParams) => {
    try {
      const response = await axios.post(`${API_URL}/search/similarity`, searchParams);
      return response.data;
    } catch (error) {
      console.error('Error performing similarity search:', error);
      throw error;
    }
  },

  /**
   * Perform a keyword search
   * @param {string} query - The search query
   * @returns {Promise} - Promise with the search results
   */
  keywordSearch: async (query) => {
    try {
      const formData = new FormData();
      formData.append('query', query);
      const response = await axios.post(`${API_URL}/search/keywords`, formData);
      return response.data;
    } catch (error) {
      console.error('Error performing keyword search:', error);
      throw error;
    }
  },

  /**
   * Get list of available document sources with metadata
   * @returns {Promise} - Promise with all available document sources
   */
  getAvailableSources: async () => {
    try {
      const response = await axios.get(`${API_URL}/documents/available-sources`);
      return response.data;
    } catch (error) {
      console.error('Error getting available sources:', error);
      throw error;
    }
  },

  /**
   * Get retrieval path data for a specific answer/message
   * @param {string} retrievalId - ID of the retrieval to get path data for
   * @returns {Promise} - Promise with the retrieval path data
   */
  getRetrievalPath: async (retrievalId) => {
    try {
      // First try directly accessing the JSON file from the retrieval_paths directory
      try {
        // Directly fetch the JSON file from the chat_session_logs/retrieval_paths directory
        const response = await axios.get(`${API_URL}/chat_session_logs/retrieval_paths/${retrievalId}.json`);
        return response.data;
      } catch (error) {
        // If direct access fails, try the API endpoint
        console.log("Direct file access failed, trying API endpoint");
        const response = await axios.get(`${API_URL}/retrieval/${retrievalId}`);
        return response.data;
      }
    } catch (error) {
      console.error('Error getting retrieval path:', error);
      throw error;
    }
  },

  /**
   * Get chunk settings configuration
   * @returns {Promise} - Promise with the chunk settings configuration
   */  getChunkSettings: async () => {
    try {
      // First try with the standard path
      try {
        const response = await axios.get(`${API_URL}/ingestion/chunk-settings`);
        return response.data;
      } catch (error) {
        // If that fails, try with the /dsapi/ prefix
        console.log("Falling back to /dsapi/ prefixed endpoint for chunk settings");
        const response = await axios.get(`${API_URL}/dsapi/ingestion/chunk-settings`);
        return response.data;
      }
    } catch (error) {
      console.error('Error getting chunk settings:', error);
      throw error;
    }
  },
  
  /**
   * Update chunk settings for document ingestion
   * @param {Object} settings - The chunk settings to update
   * @returns {Promise} - Promise with the updated settings
   */
  updateChunkSettings: async (settings) => {
    try {
      // First try with the standard path
      try {
        const response = await axios.post(`${API_URL}/ingestion/chunk-settings`, settings);
        return response.data;
      } catch (error) {
        // If that fails, try with the /dsapi/ prefix
        console.log("Falling back to /dsapi/ prefixed endpoint for updating chunk settings");
        const response = await axios.post(`${API_URL}/dsapi/ingestion/chunk-settings`, settings);
        return response.data;
      }
    } catch (error) {
      console.error('Error updating chunk settings:', error);
      throw error;
    }
  },

  /**
   * Get the ingestion rules configuration
   * @returns {Promise} - Promise with the ingestion rules
   */
  getIngestionRules: async () => {
    try {
      const response = await axios.get(`${API_URL}/ingestion/rules`);
      return response.data;
    } catch (error) {
      console.error('Error getting ingestion rules:', error);
      throw error;
    }
  },

  /**
   * Update ingestion rules
   * @param {Object} rules - The updated ingestion rules
   * @returns {Promise} - Promise with the update response
   */
  updateIngestionRules: async (rules) => {
    try {
      const response = await axios.post(`${API_URL}/ingestion/rules`, rules);
      return response.data;
    } catch (error) {
      console.error('Error updating ingestion rules:', error);
      throw error;
    }
  },

  // Admin API methods
  get: async (url, config = {}) => {
    try {
      const response = await adminAxios.get(url, config);
      return response;
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      throw error;
    }
  },

  post: async (url, data, config = {}) => {
    try {
      const response = await adminAxios.post(url, data, config);
      return response;
    } catch (error) {
      console.error(`Error posting to ${url}:`, error);
      throw error;
    }
  },

  put: async (url, data, config = {}) => {
    try {
      const response = await adminAxios.put(url, data, config);
      return response;
    } catch (error) {
      console.error(`Error updating ${url}:`, error);
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await adminAxios.delete(url, config);
      return response;
    } catch (error) {
      console.error(`Error deleting ${url}:`, error);
      throw error;
    }
  },

  /**
   * Get the current setup checklist status
   * @returns {Promise} - Promise with the setup checklist status
   */
  getSetupChecklistStatus: async () => {
    try {
      // Try to get from localStorage first for better performance
      const storedStatus = localStorage.getItem('setupChecklistStatus');
      
      if (storedStatus) {
        const parsedStatus = JSON.parse(storedStatus);
        
        // Check if all items are completed
        const allItemsCompleted = parsedStatus.items && 
          parsedStatus.items.every(item => item.completed);
        
        // If all items are completed, return the stored status
        if (allItemsCompleted) {
          return parsedStatus;
        }
        
        // If not all items are completed, always reset the dismissed flag to false
        // This ensures the checklist shows on page refresh if there are incomplete items
        return {
          ...parsedStatus,
          dismissed: false,
          lastUpdated: new Date().toISOString()
        };
      }
      
      // If not in localStorage, return default uncompleted checklist
      return { 
        items: [
          { id: 'model_configuration', title: 'Configure Model Settings', completed: false, description: 'Set up and configure the AI model settings for optimal performance' },
          { id: 'model_selection', title: 'Select AI Model', completed: false, description: 'Choose which AI model works best for your needs' },
          { id: 'test_question', title: 'Ask Test Question', completed: false, description: 'Try asking a question to test the system' },
          { id: 'configure_sources', title: 'Configure Data Sources', completed: false, description: 'Select which data sources to use for answering questions' },
          { id: 'system_settings', title: 'Review System Settings', completed: false, description: 'Check system settings for optimal performance' }
        ],
        lastUpdated: new Date().toISOString(),
        dismissed: false
      };
    } catch (error) {
      console.error('Error getting setup checklist status:', error);
      throw error;
    }
  },

  /**
   * Update the setup checklist status
   * @param {Object} statusData - The updated checklist status
   * @returns {Promise} - Promise with the updated checklist status
   */
  updateSetupChecklistStatus: async (statusData) => {
    try {
      // Store in localStorage
      localStorage.setItem('setupChecklistStatus', JSON.stringify({
        ...statusData,
        lastUpdated: new Date().toISOString()
      }));
      
      return statusData;
    } catch (error) {
      console.error('Error updating setup checklist status:', error);
      throw error;
    }
  },

  /**
   * Mark a specific checklist item as completed
   * @param {string} itemId - ID of the item to mark as completed
   * @returns {Promise} - Promise with the updated checklist status
   */
  completeChecklistItem: async (itemId) => {
    try {
      const currentStatus = await apiService.getSetupChecklistStatus();
      
      const updatedItems = currentStatus.items.map(item => 
        item.id === itemId ? { ...item, completed: true } : item
      );
      
      const updatedStatus = {
        ...currentStatus,
        items: updatedItems,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem('setupChecklistStatus', JSON.stringify(updatedStatus));
      return updatedStatus;
    } catch (error) {
      console.error('Error completing checklist item:', error);
      throw error;
    }
  },

  /**
   * Dismiss the setup checklist
   * @returns {Promise} - Promise with the updated checklist status
   */
  dismissChecklist: async () => {
    try {
      const currentStatus = await apiService.getSetupChecklistStatus();
      
      const updatedStatus = {
        ...currentStatus,
        dismissed: true,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem('setupChecklistStatus', JSON.stringify(updatedStatus));
      return updatedStatus;
    } catch (error) {
      console.error('Error dismissing checklist:', error);
      throw error;
    }
  },

  /**
   * Reset the dismissed status of the checklist
   * @returns {Promise} - Promise with the updated checklist status
   */
  resetChecklistDismissed: async () => {
    try {
      const currentStatus = await apiService.getSetupChecklistStatus();
      
      const updatedStatus = {
        ...currentStatus,
        dismissed: false,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem('setupChecklistStatus', JSON.stringify(updatedStatus));
      return updatedStatus;
    } catch (error) {
      console.error('Error resetting checklist dismissed status:', error);
      throw error;
    }
  },

  /**
   * Reset the entire checklist to its initial state
   * @returns {Promise} - Promise with the reset checklist status
   */
  resetEntireChecklist: async () => {
    try {
      // Create a fresh uncompleted checklist
      const freshChecklist = { 
        items: [
          { id: 'model_configuration', title: 'Configure Model Settings', completed: false, description: 'Set up and configure the AI model settings for optimal performance' },
          { id: 'model_selection', title: 'Select AI Model', completed: false, description: 'Choose which AI model works best for your needs' },
          { id: 'test_question', title: 'Ask Test Question', completed: false, description: 'Try asking a question to test the system' },
          { id: 'configure_sources', title: 'Configure Data Sources', completed: false, description: 'Select which data sources to use for answering questions' },
          { id: 'system_settings', title: 'Review System Settings', completed: false, description: 'Check system settings for optimal performance' }
        ],
        lastUpdated: new Date().toISOString(),
        dismissed: false
      };
      
      // Store in localStorage
      localStorage.setItem('setupChecklistStatus', JSON.stringify(freshChecklist));
      return freshChecklist;
    } catch (error) {
      console.error('Error resetting checklist:', error);
      throw error;
    }
  },

  /**
   * Get all saved prompts
   * @returns {Promise} - Promise with saved prompts data
   */
  getSavedPrompts: async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts`);
      return response.data;
    } catch (error) {
      console.error('Error getting saved prompts:', error);
      throw error;
    }
  },

  /**
   * Save a new prompt
   * @param {Object} promptData - The prompt data to save
   * @returns {Promise} - Promise with the saved prompt data including ID
   */
  savePrompt: async (promptData) => {
    try {
      const response = await axios.post(`${API_URL}/prompts`, promptData);
      return response.data;
    } catch (error) {
      console.error('Error saving prompt:', error);
      throw error;
    }
  },

  /**
   * Get a specific prompt by ID
   * @param {string} promptId - The ID of the prompt to retrieve
   * @returns {Promise} - Promise with the prompt data
   */
  getPrompt: async (promptId) => {
    try {
      const response = await axios.get(`${API_URL}/prompts/${promptId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting prompt:', error);
      throw error;
    }
  },

  /**
   * Delete a prompt by ID
   * @param {string} promptId - The ID of the prompt to delete
   * @returns {Promise} - Promise with the deletion status
   */
  deletePrompt: async (promptId) => {
    try {
      const response = await axios.delete(`${API_URL}/prompts/${promptId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  },

  /**
   * Use a prompt as the active QA_PROMPT
   * @param {string} promptId - The ID of the prompt to use
   * @returns {Promise} - Promise with the status of setting the active prompt
   */
  usePrompt: async (promptId) => {
    try {
      const response = await axios.put(`${API_URL}/prompts/${promptId}/use`);
      return response.data;
    } catch (error) {
      console.error('Error using prompt:', error);
      throw error;
    }
  },

  /**
   * Get the currently active prompt ID
   * @returns {Promise} - Promise with the active prompt ID
   */
  getActivePromptId: async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts/active`);
      return response.data;
    } catch (error) {
      console.error('Error getting active prompt ID:', error);
      throw error;
    }
  },

  /**
   * Fetch the agent's reasoning chain for a given query. Can be called in
   * parallel with askQuestion so the UI can animate the model's chain-of-thought
   * while waiting for the final answer.
   * @param {string} question - User question
   * @param {string} sessionId - Conversation/session identifier (should match askQuestion)
   * @returns {Promise<object>} - {{ reasoning: Array }}
   */
  getReasoning: async (question, sessionId) => {
    try {
      const response = await fetch(`${API_URL}/reasoning`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, id: sessionId })
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching reasoning chain:', error);
      // Return empty reasoning array on error
      return { reasoning: [] };
    }
  },

  /**
   * Stream the agent's reasoning process in real-time using Server-Sent Events.
   * 
   * @param {string} question - The user's question
   * @param {string} sessionId - Session identifier
   * @param {function} onStep - Callback function called for each reasoning step
   * @param {function} onComplete - Callback function called when streaming completes
   * @param {function} onError - Callback function called on error
   * @returns {function} - Function to close the stream
   */
  streamReasoning: (question, sessionId, onStep, onComplete, onError) => {
  let reader = null;
  let isStreamActive = true;
  let buffer = ''; // For incomplete JSON lines

  try {
    fetch(`${API_URL}/reasoning/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify({ question, id: sessionId }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        reader = response.body.getReader();
        const decoder = new TextDecoder();

        function read() {
          if (!isStreamActive) return;

          return reader.read().then(({ done, value }) => {
            if (done || !isStreamActive) {
              onComplete && onComplete();
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop(); // Keep incomplete last line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const jsonPart = line.slice(6).trim();

                if (!jsonPart) continue;

                try {
                  const parsed = JSON.parse(jsonPart);

                  if (parsed.type === 'stream_end') {
                    onComplete && onComplete();
                    return;
                  } else if (parsed.type === 'error') {
                    onError && onError(parsed.content?.details?.error || 'Unknown error');
                    return;
                  } else {
                    onStep && onStep(parsed);
                  }
                } catch (err) {
                  console.warn('Skipping incomplete JSON line (will retry next chunk):', err.message);
                  buffer = `data: ${jsonPart}\n` + buffer; // Re-add to buffer
                  break; // Wait for next chunk
                }
              }
            }

            return read(); // Continue reading
          });
        }

        return read();
      })
      .catch(err => {
        console.error('Streaming fetch error:', err);
        onError && onError(err.message);
      });

    // Cleanup function
    return () => {
      isStreamActive = false;
      if (reader) {
        reader.cancel().catch(err => {
          console.warn('Error canceling stream:', err);
        });
      }
    };
  } catch (err) {
    console.error('Failed to setup stream:', err);
    onError && onError(err.message);
    return () => {};
  }
},

};

export default apiService;
